import type { ChakraStylesConfig, GroupBase } from "chakra-react-select"
import {
  AUGMENTATION_PANEL_AVATAR_CSS,
  AUGMENTATION_PANEL_MENU_CONTENT_PROPS,
  AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS,
  AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_CSS,
  AUGMENTATION_PANEL_SELECT_PROPS
} from "./augmentationPanelStyles"

const SELECT_TEXT_CSS = { textStyle: "augmentationPanel" } as const

// The control is a fixed-height pill, so anything that could wrap has to
// truncate instead — a second line gets clipped rather than growing the field.
const TRUNCATE_CSS = {
  maxW: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
} as const

const NO_FOCUS_RING_CSS = { boxShadow: "none", outline: "none" } as const

const CONTROL_CSS = {
  ...AUGMENTATION_PANEL_SELECT_PROPS,
  border: "none",
  minH: AUGMENTATION_PANEL_SELECT_PROPS.h,
  // Focusing the control opens the menu, which is indication enough. A ring
  // here also paints outside the pill and gets clipped by the panel edge.
  outline: "none",
  _focus: NO_FOCUS_RING_CSS,
  _focusVisible: NO_FOCUS_RING_CSS,
  _hover: { bg: "augmentationPanel.fill.strong" },
  // The leading icon renders in a gutter sized to the input height, which
  // pushes it out of line with the list items above it. Matched by its direct
  // icon child since the library emits no class names here.
  "& > div:has(> svg)": {
    alignSelf: "center",
    h: "augmentationPanelIcon",
    justifyContent: "flex-start",
    mr: "augmentationPanel",
    minW: "augmentationPanelIcon",
    w: "augmentationPanelIcon",
    "& svg": AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS
  }
} as const

/**
 * Styles `ItemSelector` (chakra-react-select) to match panel fields and menus.
 * The library emits emotion-only class names, so this has to go through
 * `chakraStyles` rather than CSS selectors.
 *
 * A function rather than a constant so it carries the caller's option type:
 * `ChakraStylesConfig` places the option in contravariant positions, so a
 * config built for one option type can't be handed to a selector over another.
 *
 * Note these merge by key over the shared defaults, so each override restates
 * everything it needs.
 */
export function augmentationPanelSelectorChakraStyles<
  Option
>(): ChakraStylesConfig<Option, boolean, GroupBase<Option>> {
  return {
    container: (provided) => ({
      ...provided,
      w: "100%"
    }),
    control: (provided) => ({
      ...provided,
      ...CONTROL_CSS
    }),
    valueContainer: (provided) => ({
      ...provided,
      flexWrap: "nowrap",
      gap: 0,
      px: 0,
      py: 0
    }),
    placeholder: (provided) => ({
      ...provided,
      ...SELECT_TEXT_CSS,
      ...TRUNCATE_CSS,
      m: 0,
      // Placeholders are static copy (Figma: 60% opacity).
      opacity: 0.6
    }),
    singleValue: (provided) => ({
      ...provided,
      ...SELECT_TEXT_CSS,
      ...TRUNCATE_CSS,
      m: 0,
      // The selected item renders its name in a Text colored for dark surfaces.
      "& .ItemSelector__name": { color: "augmentationPanel.text" }
    }),
    input: (provided) => ({
      ...provided,
      ...SELECT_TEXT_CSS,
      m: 0
    }),
    indicatorSeparator: () => ({ display: "none" }),
    clearIndicator: () => ({ display: "none" }),
    dropdownIndicator: (provided) => ({
      ...provided,
      bg: "transparent",
      color: "augmentationPanel.text",
      minW: "augmentationPanelIcon",
      opacity: 0.6,
      px: 0,
      w: "augmentationPanelIcon"
    }),
    menu: (provided) => ({
      ...provided,
      mb: "5px",
      mt: "5px",
      w: "100%",
      // Keeps the menu above sibling panel sections, which are positioned and
      // would otherwise paint over it.
      zIndex: "popover"
    }),
    menuList: (provided) => ({
      ...provided,
      ...AUGMENTATION_PANEL_MENU_CONTENT_PROPS,
      // Nearly opaque: this menu floats over the panel's own list rather than
      // page content, and the panel text ghosting through it reads as noise.
      bg: "augmentationPanel.menuSurfaceSolid",
      maxH: "260px",
      overflowY: "auto",
      w: "100%"
    }),
    option: (provided, state) => ({
      ...provided,
      ...AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_CSS,
      bg:
        state.isFocused || state.isSelected
          ? "augmentationPanel.fill.muted"
          : "transparent",
      // Menu rows list the same kind of thing as the panel's list items, so
      // they take that row height rather than the tighter options-menu row.
      minH: "augmentationPanelRow",
      // Rows lead with an icon, an avatar, or an image depending on the item.
      // Sizing them all to the panel's icon slot keeps every label on the same
      // left edge as the control and the list items above it.
      "& .chakra-stack": { gap: "augmentationPanel" },
      "& .Pathicon": AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS,
      "& .chakra-avatar__root": AUGMENTATION_PANEL_AVATAR_CSS,
      "& .chakra-image": {
        borderRadius: "4px",
        boxSize: "augmentationPanelIcon",
        maxH: "augmentationPanelIcon",
        minW: "augmentationPanelIcon",
        objectFit: "cover"
      }
    }),
    groupHeading: (provided) => ({
      ...provided,
      ...SELECT_TEXT_CSS,
      opacity: 0.6,
      px: "augmentationPanelGutter"
    })
  }
}

// No hover means touch, where the resting row height can't host a comfortable
// target. Written as a raw query rather than the theme's `_mediaHoverNone`
// condition, which isn't in the generated types yet.
const NO_HOVER_MEDIA = "@media (hover: none)"

/** Sizing for controls riding inside the taller no-hover add row. */
export const AUGMENTATION_PANEL_ADD_CONTROL_TOUCH_CSS = {
  [NO_HOVER_MEDIA]: {
    h: "augmentationPanelRowTouch",
    minW: "40px"
  }
} as const

/**
 * The inline add form's selector. Its confirm and cancel controls ride inside
 * the control, in the same trailing slot a list item's options menu occupies —
 * so a pending item reads as a row waiting to be committed rather than a field
 * with buttons floating under it.
 */
export function augmentationPanelAddSelectorChakraStyles<
  Option
>(): ChakraStylesConfig<Option, boolean, GroupBase<Option>> {
  return {
    ...augmentationPanelSelectorChakraStyles<Option>(),
    control: (provided) => ({
      ...provided,
      ...CONTROL_CSS,
      // The trailing controls supply their own edge spacing.
      pr: "4px",
      // The row grows to give both controls a native-sized target. It's a
      // transient state, so a taller row here doesn't disturb the list.
      [NO_HOVER_MEDIA]: {
        // A pill at whatever the touch row height is.
        borderRadius: "full",
        h: "augmentationPanelRowTouch",
        minH: "augmentationPanelRowTouch"
      }
    }),
    // The cancel control takes the chevron's place. The menu already opens on
    // focus and on a click anywhere in the control, so nothing is lost.
    dropdownIndicator: () => ({ display: "none" })
  }
}
